<template>
  <wizzard-container :infos="infos">
    <div class="title">{{ $t('Opening Hours') }}</div>
    <p class="caption">{{ $t('Opening Hours Caption') }}</p>

    <div v-for="(day, index) in days" :key="index" class="mb-4">
      <v-row align="center">
        <v-col cols="12" class="d-flex align-center justify-space-between">
          <div class="text-capitalize">{{ day.label }}</div>
          <v-switch
            v-model="form.opening_hours_data[index].is_open"
            :label="$t('Open')"
            hide-details
            class="mt-0 pt-0 ml-6"
          />
        </v-col>
        <v-col cols="12">
          <div v-if="form.opening_hours_data[index].is_open" class="d-flex flex-row">
            <v-select
              v-model="form.opening_hours_data[index].open_hour"
              :items="hours"
              :label="$t('Hour')"
              dense
              outlined
              hide-details
              class="mr-2"
              style="max-width: 80px"
            />
            <v-select
              v-model="form.opening_hours_data[index].open_minute"
              :items="minutes"
              :label="$t('Min')"
              dense
              outlined
              hide-details
              class="mr-4"
              style="max-width: 80px"
            />
            <span class="mr-4">-</span>
            <v-select
              v-model="form.opening_hours_data[index].close_hour"
              :items="hours"
              :label="$t('Hour')"
              dense
              outlined
              hide-details
              class="mr-2"
              style="max-width: 80px"
            />
            <v-select
              v-model="form.opening_hours_data[index].close_minute"
              :items="minutes"
              :label="$t('Min')"
              dense
              outlined
              hide-details
              style="max-width: 80px"
            />
          </div>
          <template v-else>
            <span class="grey--text">{{ $t(notSet ? 'Not set' : 'Closed') }}</span>
          </template>
        </v-col>
      </v-row>
      <v-divider v-if="index < 6" class="mt-6" />
    </div>

    <form-action-buttons
      status="listed"
      @submit="submit"
    />
  </wizzard-container>
</template>

<script>
import axios from 'axios';
import FormValidationMixin from '@/lib/FormValidationMixin';
import FormActionButtons from '@/views/ads/forms/FormActionButtons.vue';
import WizzardContainer from '@/views/ads/forms/WizzardContainer.vue';
import DateFormatMixin from '@/lib/DateFormatMixin';

export default {
  mixins: [FormValidationMixin, DateFormatMixin],
  components: {
    FormActionButtons,
    WizzardContainer,
  },
  props: {
    businessId: { type: String, required: true },
    business: { type: Object, default: null },
  },
  data() {
    return {
      form: {
        opening_hours_data: Array.from({ length: 7 }).map((_, i) => ({
          day: i,
          is_open: false,
          open_hour: 8,
          open_minute: 0,
          close_hour: 16,
          close_minute: 0,
        })),
      },
      hours: Array.from({ length: 24 }).map((_, i) => ({ text: String(i).padStart(2, '0'), value: i })),
      minutes: Array.from({ length: 60 }).map((_, i) => ({ text: String(i).padStart(2, '0'), value: i })),
    };
  },
  computed: {
    days() {
      return Array.from({ length: 7 }).map((_, i) => ({
        label: this.getDayName(i + 1),
      }));
    },
    notSet() {
      return this.business && (!this.business.opening_hours || !this.business.opening_hours.length);
    },
    infos() {
      return [
        {
          icon: 'green-energy',
          title: 'Business Form Opening Hours InfoTitle',
          description: 'Business Form Opening Hours InfoDescription',
        },
      ];
    },
  },
  watch: {
    business: {
      immediate: true,
      handler() {
        this.setFormData();
      },
    },
  },
  methods: {
    setFormData() {
      if (!this.business || !this.business.opening_hours) {
        return;
      }

      const openingHours = this.business.opening_hours;
      const newOpeningHoursData = [...this.form.opening_hours_data];

      openingHours.forEach((oh) => {
        if (newOpeningHoursData[oh.day]) {
          newOpeningHoursData[oh.day] = {
            ...newOpeningHoursData[oh.day],
            is_open: true,
            open_hour: oh.open_hour,
            open_minute: oh.open_minute,
            close_hour: oh.close_hour,
            close_minute: oh.close_minute,
          };
        }
      });

      this.form.opening_hours_data = newOpeningHoursData;
    },
    submit() {
      this.$store.dispatch('loading', true);

      const payload = {
        opening_hours: this.form.opening_hours_data
          .filter((d) => d.is_open)
          .map((d) => ({
            day: d.day,
            open_hour: d.open_hour,
            open_minute: d.open_minute,
            close_hour: d.close_hour,
            close_minute: d.close_minute,
          })),
      };

      axios
        .post(`panel/ads/validation/business/opening-hours/${this.businessId}`, payload)
        .then(() => {
          this.$store.dispatch('clearErrors');
          this.$store.dispatch('message', this.$t('Saved successfully'));
          this.$emit('success');
        })
        .catch((error) => {
          const errors = error.response && error.response.data && error.response.data.errors
            ? error.response.data.errors
            : {};
          this.$store.dispatch('setErrors', errors);
        })
        .finally(() => {
          this.$store.dispatch('loading', false);
        });
    },
  },
};
</script>
